import NavigationBar from "../NewLandingPage/NavigationBar";
import { Card, Row, Col, Typography, Tag, Button, Divider, Statistic } from "antd";
import {
    SafetyOutlined,
    FundOutlined,
    DollarOutlined,
    TeamOutlined,
    CheckCircleOutlined,
    RocketOutlined,
    RobotOutlined,
    LineChartOutlined,
    StarOutlined,
    UserOutlined,
    GlobalOutlined,
    ClockCircleOutlined
} from "@ant-design/icons";
import styles from "./MarketPlaceLandingPage.module.scss";

const { Title, Text, Paragraph } = Typography;

const businessTools = [
    {
        title: "Risk Assessment Framework",
        description: "Comprehensive risk evaluation and management system for identifying and mitigating business risks.",
        icon: <SafetyOutlined style={{ fontSize: '24px', color: '#1890ff' }} />,
        features: ["Risk Identification", "Impact Analysis", "Mitigation Planning", "Real-time Monitoring"]
    },
    {
        title: "Strategic Planning Toolkit",
        description: "End-to-end strategic planning solution for business growth and development.",
        icon: <FundOutlined style={{ fontSize: '24px', color: '#52c41a' }} />,
        features: ["Goal Setting", "Resource Allocation", "Performance Tracking", "Strategic Alignment"]
    },
    {
        title: "Financial Model Builder",
        description: "Advanced financial modeling and forecasting tool for business planning.",
        icon: <DollarOutlined style={{ fontSize: '24px', color: '#722ed1' }} />,
        features: ["Financial Forecasting", "Scenario Analysis", "Budget Planning", "Investment Evaluation"]
    },
    {
        title: "Sales Enablement Suite",
        description: "Complete sales toolkit for improving team performance and closing more deals.",
        icon: <RocketOutlined style={{ fontSize: '24px', color: '#fa8c16' }} />,
        features: ["Sales Pipeline", "Lead Management", "Performance Analytics", "Team Collaboration"]
    },
    {
        title: "Team Onboarding System",
        description: "Streamlined onboarding process for new team members and efficient team management.",
        icon: <TeamOutlined style={{ fontSize: '24px', color: '#13c2c2' }} />,
        features: ["Documentation", "Training Modules", "Progress Tracking", "Integration Support"]
    },
    {
        title: "Compliance Management System",
        description: "Comprehensive compliance tracking and management solution for regulatory requirements.",
        icon: <CheckCircleOutlined style={{ fontSize: '24px', color: '#eb2f96' }} />,
        features: ["Regulatory Compliance", "Audit Trails", "Policy Management", "Risk Assessment"]
    },
    {
        title: "Fundraising Toolkit",
        description: "Complete toolkit for managing fundraising campaigns and investor relations.",
        icon: <DollarOutlined style={{ fontSize: '24px', color: '#faad14' }} />,
        features: ["Investor Relations", "Campaign Management", "Document Preparation", "Progress Tracking"]
    },
    {
        title: "AI Business Toolkit",
        description: "AI-powered tools for business automation and intelligent decision making.",
        icon: <RobotOutlined style={{ fontSize: '24px', color: '#2f54eb' }} />,
        features: ["Process Automation", "Data Analysis", "Predictive Analytics", "Smart Insights"]
    },
    {
        title: "Business Forecasting System",
        description: "Advanced forecasting and trend analysis for business planning and strategy.",
        icon: <LineChartOutlined style={{ fontSize: '24px', color: '#f5222d' }} />,
        features: ["Trend Analysis", "Market Prediction", "Performance Metrics", "Strategic Planning"]
    }
];

const MarketPlaceLandingPage = () => {
    return (
        <div>
            <NavigationBar />
            <div className={styles.marketplaceContainer}>
                {/* Hero Section */}
                <div className={styles.header}>
                    <Title className={styles.title}>Business Tools Marketplace</Title>
                    <Text className={styles.description}>
                        Discover our comprehensive suite of business tools designed to enhance your organization's efficiency,
                        drive growth, and ensure compliance. Each tool is crafted to address specific business needs and
                        challenges.
                    </Text>
                    <div className={styles.statsContainer}>
                        <Row gutter={[32, 32]} justify="center">
                            <Col xs={12} sm={8} md={6}>
                                <Statistic
                                    title="Active Users"
                                    value={10000}
                                    prefix={<UserOutlined />}
                                    suffix="+"
                                />
                            </Col>
                            <Col xs={12} sm={8} md={6}>
                                <Statistic
                                    title="Global Reach"
                                    value={150}
                                    prefix={<GlobalOutlined />}
                                    suffix="+ Countries"
                                />
                            </Col>
                            <Col xs={12} sm={8} md={6}>
                                <Statistic
                                    title="Success Rate"
                                    value={98}
                                    prefix={<StarOutlined />}
                                    suffix="%"
                                />
                            </Col>
                            <Col xs={12} sm={8} md={6}>
                                <Statistic
                                    title="Response Time"
                                    value={24}
                                    prefix={<ClockCircleOutlined />}
                                    suffix="hrs"
                                />
                            </Col>
                        </Row>
                    </div>
                </div>

                {/* Why Choose Us Section */}
                <div className={styles.section}>
                    <Title level={2} className={styles.sectionTitle}>Why Choose Our Business Tools?</Title>
                    <Row gutter={[24, 24]}>
                        <Col xs={24} md={8}>
                            <Card className={styles.featureCard}>
                                <Title level={4}>Expert-Backed Solutions</Title>
                                <Paragraph>
                                    Our tools are developed by industry experts with decades of experience in business management,
                                    ensuring you get the most effective solutions for your needs.
                                </Paragraph>
                            </Card>
                        </Col>
                        <Col xs={24} md={8}>
                            <Card className={styles.featureCard}>
                                <Title level={4}>Seamless Integration</Title>
                                <Paragraph>
                                    All tools are designed to work together seamlessly, allowing you to create a unified
                                    business management ecosystem that fits your workflow.
                                </Paragraph>
                            </Card>
                        </Col>
                        <Col xs={24} md={8}>
                            <Card className={styles.featureCard}>
                                <Title level={4}>Continuous Updates</Title>
                                <Paragraph>
                                    We regularly update our tools with new features and improvements based on user feedback
                                    and industry developments.
                                </Paragraph>
                            </Card>
                        </Col>
                    </Row>
                </div>

                <Divider />

                {/* Tools Grid Section */}
                <div className={styles.section}>
                    <Title level={2} className={styles.sectionTitle}>Explore Our Business Tools</Title>
                    <Text className={styles.sectionDescription}>
                        Browse our collection of powerful business tools, each designed to address specific challenges
                        and drive your business forward. From risk assessment to AI-powered analytics, we've got you covered.
                    </Text>
                    <div className={styles.toolsGrid}>
                        <Row gutter={[24, 24]}>
                            {businessTools.map((tool, index) => (
                                <Col xs={24} sm={12} md={8} key={index}>
                                    <Card
                                        className={styles.toolCard}
                                        hoverable
                                    >
                                        <div className={styles.cardContent}>
                                            <div style={{ marginBottom: '16px' }}>
                                                {tool.icon}
                                            </div>
                                            <Title level={4} className={styles.toolTitle}>
                                                {tool.title}
                                            </Title>
                                            <Text className={styles.toolDescription}>
                                                {tool.description}
                                            </Text>
                                            <div className={styles.toolFeatures}>
                                                {tool.features.map((feature, idx) => (
                                                    <Tag
                                                        key={idx}
                                                        className={styles.featureTag}
                                                        color="blue"
                                                    >
                                                        {feature}
                                                    </Tag>
                                                ))}
                                            </div>
                                            <Button type="primary" className={styles.toolButton}>
                                                Learn More
                                            </Button>
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </div>
                </div>

                {/* Call to Action Section */}
                <div className={styles.ctaSection}>
                    <Title level={2}>Ready to Transform Your Business?</Title>
                    <Paragraph className={styles.ctaDescription}>
                        Join thousands of successful businesses that have already enhanced their operations with our tools.
                        Start your journey today and experience the difference.
                    </Paragraph>
                    <Button type="primary" size="large" className={styles.ctaButton}>
                        Get Started Now
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default MarketPlaceLandingPage;